/**
 * De etiketten van de tijdmachine, los van het scherm zodat ze op UITKOMST te
 * toetsen zijn in plaats van op bronregels.
 *
 * **HIER ZIT DE TIJDZONEVAL VAN 12 AUGUSTUS 2026, EN DAT IS DE REDEN DAT DIT
 * BESTAND BESTAAT.**
 *
 * JavaScript leest twee vormen verschillend, en het verschil is niet zichtbaar
 * tot iemand ten westen van Greenwich kijkt:
 *
 *     new Date("2026-08-24")            -> middernacht UTC
 *     new Date("2026-08-24T10:00:00")   -> 10 uur LOKALE tijd
 *
 * De dagindex levert KALE datums, dus de eerste vorm. In New York wordt
 * middernacht UTC `2026-08-23T20:00`, en dan toont een daglijst 23 augustus
 * boven berichten van 24 augustus. Op deze machine is dat onzichtbaar, want
 * Amsterdam ligt OOSTELIJK van UTC en schuift de andere kant op.
 *
 * `dagLabel` ontleedt de tekenreeks daarom ZELF en maakt er nooit een tijdstip
 * van. De toets ernaast draait in een westelijke tijdzone in een APART PROCES,
 * met een ijking dat de naieve vorm daar wel degelijk verspringt.
 */

const MAANDEN = [
  "januari", "februari", "maart", "april", "mei", "juni",
  "juli", "augustus", "september", "oktober", "november", "december",
];

/** De groep waarin een datum belandt die niet te lezen is. Zie `maandGroepen`:
 *  onleesbaar is een MELDING en geen reden om iets weg te laten. */
const ONBEKENDE_MAAND = "?";

/**
 * `2026-08-24` wordt `24 augustus 2026`.
 *
 * Zonder `new Date`, met opzet. Zie de kop van dit bestand: een kale datum door
 * `new Date` halen is precies de fout die dit etiket zou verschuiven.
 *
 * Onbekende invoer komt ONGEWIJZIGD terug in plaats van als `NaN` of `Invalid
 * Date`. Een scherm dat "Invalid Date" toont is erger dan een scherm dat de
 * ruwe waarde toont: het tweede is te herkennen, het eerste niet.
 */
export function dagLabel(datum) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(datum || ""));
  if (!m) return String(datum || "");
  const [, jaar, maand, dag] = m;
  const naam = MAANDEN[Number(maand) - 1];
  if (!naam) return String(datum);
  return `${Number(dag)} ${naam} ${jaar}`;
}

/**
 * `2026-08-24T14:05:00` wordt `14:05`.
 *
 * De backend geeft een tijdstempel ZONDER tijdzone terug, dus dit is de tijd
 * zoals hij is opgeslagen. Die wordt hier niet omgerekend: Frank leest zijn
 * eigen geschiedenis op zijn eigen machine en wil de klok zien die hij zag toen
 * hij het typte. Omrekenen zou een dienst zijn die niemand heeft gevraagd, en
 * bij een naieve tijdstempel is er ook geen zone om vanaf te rekenen.
 *
 * Daarom weer met de hand geknipt en niet via `new Date`: dat zou van dezelfde
 * tekenreeks een tijdstip in de LOKALE zone maken, en dat is een andere vraag.
 */
export function tijdLabel(tijdstempel) {
  const m = /^\d{4}-\d{2}-\d{2}[T ](\d{2}):(\d{2})/.exec(String(tijdstempel || ""));
  if (!m) return "";
  return `${m[1]}:${m[2]}`;
}

/**
 * `2026-08` wordt `augustus 2026`.
 *
 * Zelfde regel als `dagLabel`: zelf ontleden, nooit door `new Date`. Een maand
 * zonder dag zou daar `2026-08-01T00:00Z` worden, en dan heet augustus in
 * Honolulu juli.
 */
export function maandLabel(maand) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(maand || ""));
  if (!m) return String(maand || "");
  const naam = MAANDEN[Number(m[2]) - 1];
  if (!naam) return String(maand);
  return `${naam} ${m[1]}`;
}

/**
 * HOEVEEL HELE DAGRIJEN ER IN HET VENSTER PASSEN.
 *
 * **GEMETEN OP HET DRAAIENDE SCHERM op 24 augustus 2026**, in een echte browser
 * met de gebouwde stylesheet en de klassen uit `TimeMachine.jsx`, op 390 en op
 * 957 met dezelfde uitkomst:
 *
 *     venster (max-h-[460px])   460 px
 *     rijhoogte                  48 px
 *     rijstap (incl. space-y-1)  52 px
 *     460 / 52 = 8,8            ->  8 hele rijen
 *
 * Dit getal was eerst 9, AFGELEID uit de klassen. Franks opdracht was het te
 * meten voor het werd vastgezet, en de meting gaf 8. Verandert de rijhoogte of
 * de vensterhoogte, dan hoort dit getal opnieuw gemeten te worden en niet
 * bijgeschat; de wachter ernaast eist dat de klassen nog dezelfde zijn.
 */
export const RIJEN_IN_BEELD = 8;

/**
 * De platte daglijst van de backend wordt een lijst maanden.
 *
 * De backend geeft `[{date: "2026-08-24", count: 30}, ...]`, nieuwste eerst, en
 * ALLE dagen die iets hebben. Maanden zijn daaruit af te leiden; er is dus geen
 * route bij nodig, en dat is gemeten en geen aanname.
 *
 * **WANNEER EEN MAAND INGEKLAPT BEGINT, EN WAAROM NIET ALTIJD.** Franks eis van
 * 24 augustus 2026: inklappen pas boven de gemeten drempel. Er valt niets te
 * winnen met een inklapbare kop boven een lijst die toch al in beeld past; dan
 * is het een klik die alleen maar iets wegneemt. De drempel is dus: passen de
 * koppen plus alle dagen samen in het venster, dan staat alles open.
 *
 * **DE NIEUWSTE MAAND STAAT ALTIJD OPEN**, ook boven de drempel. Terugbladeren
 * begint bij nu, en een tijdmachine die dichtgeklapt opengaat toont bij het
 * openen niets van wat er net is gezegd.
 *
 * **EEN ONLEESBARE DATUM WORDT NIET WEGGEGOOID.** Hij komt in een eigen groep
 * onderaan. Stil laten vallen wat je niet begrijpt is precies hoe 68 gesprekken
 * gisteren onbereikbaar waren; een zichtbare rare rij is een melding, een
 * ontbrekende rij is niets.
 *
 * @param dagen   `[{date, count}]` zoals de backend hem geeft
 * @param keuzes  wat de gebruiker zelf heeft open- of dichtgeklapt,
 *                `{"2026-07": true}`. Wint altijd van de standaard.
 * @param rijenInBeeld  de gemeten drempel; alleen anders in een toets
 */
export function maandGroepen(dagen, { keuzes = {}, rijenInBeeld = RIJEN_IN_BEELD } = {}) {
  const lijst = Array.isArray(dagen) ? dagen : [];
  const perMaand = new Map();

  for (const d of lijst) {
    const datum = String((d && d.date) || "");
    const leesbaar = /^\d{4}-\d{2}-\d{2}$/.test(datum);
    const sleutel = leesbaar ? datum.slice(0, 7) : ONBEKENDE_MAAND;
    if (!perMaand.has(sleutel)) perMaand.set(sleutel, []);
    perMaand.get(sleutel).push({ date: datum, count: Number(d && d.count) || 0 });
  }

  const sleutels = [...perMaand.keys()]
    .filter((s) => s !== ONBEKENDE_MAAND)
    .sort()
    .reverse();
  if (perMaand.has(ONBEKENDE_MAAND)) sleutels.push(ONBEKENDE_MAAND);

  const groepen = sleutels.map((sleutel) => {
    const dagenVan = perMaand.get(sleutel).slice().sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
    return {
      maand: sleutel,
      label: sleutel === ONBEKENDE_MAAND ? "Onbekende datum" : maandLabel(sleutel),
      onbekend: sleutel === ONBEKENDE_MAAND,
      dagen: dagenVan,
      aantalDagen: dagenVan.length,
      aantalBerichten: dagenVan.reduce((t, d) => t + d.count, 0),
    };
  });

  // De som die de drempel toetst: elke maandkop is zelf ook een rij.
  const rijenUitgeklapt = groepen.length + groepen.reduce((t, g) => t + g.aantalDagen, 0);
  const allesPast = rijenUitgeklapt <= rijenInBeeld;

  return groepen.map((g, i) => {
    const standaard = allesPast || i === 0;
    const keuze = Object.prototype.hasOwnProperty.call(keuzes || {}, g.maand)
      ? Boolean(keuzes[g.maand])
      : null;
    return { ...g, open: keuze === null ? standaard : keuze, rijenUitgeklapt, allesPast };
  });
}

/**
 * HOEVEELSTE DAG SINDS 1 JANUARI 1970, uit de tekenreeks zelf gerekend.
 *
 * Dit bestaat om twee dagen van elkaar af te kunnen trekken zonder `new Date`.
 * De rest van dit bestand vermijdt `Date` vanwege de tijdzoneval; hier zou hij
 * bovendien een tweede kwaad meebrengen, want een verschil tussen twee
 * tijdstippen loopt over een zomertijdgrens niet in hele dagen.
 *
 * De rekenwijze is die van Howard Hinnant (`days_from_civil`): alleen gehele
 * getallen, geldig voor elke datum, en de schrikkelregel zit erin. De toets
 * ernaast ijkt hem tegen `Date.UTC`, wat wel mag omdat dat per definitie in UTC
 * rekent en dus geen zone kent.
 */
export function dagNummer(datum) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(datum || ""));
  if (!m) return null;
  const maand = Number(m[2]);
  const dag = Number(m[3]);
  if (maand < 1 || maand > 12 || dag < 1 || dag > 31) return null;
  const jaar = Number(m[1]) - (maand <= 2 ? 1 : 0);
  const eeuw = Math.floor(jaar / 400);
  const jaarInEeuw = jaar - eeuw * 400;
  const dagInJaar = Math.floor((153 * (maand + (maand > 2 ? -3 : 9)) + 2) / 5) + dag - 1;
  const dagInEeuw =
    jaarInEeuw * 365 + Math.floor(jaarInEeuw / 4) - Math.floor(jaarInEeuw / 100) + dagInJaar;
  return eeuw * 146097 + dagInEeuw - 719468;
}

/** Hoeveel dagen die maand heeft. Met de hand, zelfde reden als hierboven. */
function dagenInMaand(jaar, maand) {
  if (maand === 2) {
    const schrikkel = (jaar % 4 === 0 && jaar % 100 !== 0) || jaar % 400 === 0;
    return schrikkel ? 29 : 28;
  }
  return [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][maand - 1];
}

/** `mrt` is de enige gangbare afkorting die geen begin van de naam is. */
const AFKORTINGEN = { mrt: "maart", sept: "september" };

/**
 * Welke maand bedoelt iemand die `juli`, `jul` of `Juli` typt? Alleen als het
 * antwoord er EEN is.
 *
 * **DE ONDERGRENS IS TWEE TEKENS, EN DAT IS EEN GEMETEN KEUZE.** Zij stond
 * eerst op drie, en toen was de uitzonderingstak hieronder ONBEREIKBAAR:
 *
 *     bij 2 tekens dubbelzinnig:  ju -> juni, juli
 *     bij 3 tekens dubbelzinnig:  GEEN
 *     bij 4 tekens dubbelzinnig:  GEEN
 *
 * Bij drie tekens bestaat er in het Nederlands geen enkele dubbelzinnige
 * afkorting, dus `treffers.length !== 1` kon alleen nog "nul treffers"
 * betekenen. Een mutatie die er `=== 0` van maakte liet op 24 augustus 2026
 * alle 62 toetsen groen, en dat was de vondst: mijn eigen toets gebruikte
 * `ma`, en `mei` begint op `me`, dus `ma` was nooit dubbelzinnig. De toets
 * slaagde op de LENGTE-eis en niet op de eis die zij beweerde te toetsen.
 *
 * Met twee tekens werkt `ma`, `me`, `ok` en de rest, en wordt `ju` geweigerd
 * om de JUISTE reden: juni en juli zijn allebei mogelijk en gokken hoort hier
 * niet. Een teken is nog steeds te weinig.
 */
function maandUitNaam(woord) {
  const w = String(woord || "").toLowerCase().replace(/\.$/, "");
  if (!w) return null;
  const heel = AFKORTINGEN[w] || w;
  const treffers = MAANDEN.filter((naam) => naam === heel || (heel.length >= 2 && naam.startsWith(heel)));
  if (treffers.length !== 1) return null;
  return MAANDEN.indexOf(treffers[0]) + 1;
}

/**
 * WAT HEEFT IEMAND IN HET DATUMVELD GETYPT?
 *
 * Geeft `{soort: "maand", maand}` of `{soort: "dag", datum, maand}`, en `null`
 * zodra het niet met zekerheid te lezen is. **Nooit een gok.** Een veld dat bij
 * onzin toch ergens heen springt is erger dan een veld dat zegt dat het de
 * invoer niet begrijpt: de eerste liegt, de tweede is te herkennen.
 *
 * Herkende vormen, en dit is de hele lijst:
 *
 *     2026-07        2026-7        juli 2026     jul 2026
 *     2026-07-15     15-07-2026    15/07/2026    15 juli 2026
 *
 * **EEN KALE MAANDNAAM ZONDER JAARTAL GEEFT `null`.** "juli" zou het jaar
 * moeten raden, en raden is precies wat hier niet hoort. Het scherm zegt welke
 * vormen wel werken.
 *
 * **EEN ONMOGELIJKE DATUM GEEFT OOK `null`**, en dat is 30 februari net zo goed
 * als maand 13. `new Date("2026-02-30")` zou daar 2 maart van maken en dan
 * springt het veld naar een dag die niemand heeft gevraagd.
 */
export function leesDatum(tekst) {
  const t = String(tekst || "").trim().replace(/\s+/g, " ");
  if (!t) return null;

  const maandSleutel = (jaar, maand) => `${jaar}-${String(maand).padStart(2, "0")}`;
  const dagSleutel = (jaar, maand, dag) => `${maandSleutel(jaar, maand)}-${String(dag).padStart(2, "0")}`;
  const geldigeDag = (jaar, maand, dag) =>
    maand >= 1 && maand <= 12 && dag >= 1 && dag <= dagenInMaand(jaar, maand);

  // 2026-07-15
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
  if (m) {
    const [jaar, maand, dag] = [Number(m[1]), Number(m[2]), Number(m[3])];
    if (!geldigeDag(jaar, maand, dag)) return null;
    return { soort: "dag", datum: dagSleutel(jaar, maand, dag), maand: maandSleutel(jaar, maand) };
  }

  // 15-07-2026 en 15/07/2026
  m = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/.exec(t);
  if (m) {
    const [dag, maand, jaar] = [Number(m[1]), Number(m[2]), Number(m[3])];
    if (!geldigeDag(jaar, maand, dag)) return null;
    return { soort: "dag", datum: dagSleutel(jaar, maand, dag), maand: maandSleutel(jaar, maand) };
  }

  // 15 juli 2026
  m = /^(\d{1,2}) ([a-zA-Z.]+) (\d{4})$/.exec(t);
  if (m) {
    const maand = maandUitNaam(m[2]);
    const [dag, jaar] = [Number(m[1]), Number(m[3])];
    if (!maand || !geldigeDag(jaar, maand, dag)) return null;
    return { soort: "dag", datum: dagSleutel(jaar, maand, dag), maand: maandSleutel(jaar, maand) };
  }

  // 2026-07 en 2026-7
  m = /^(\d{4})-(\d{1,2})$/.exec(t);
  if (m) {
    const maand = Number(m[2]);
    if (maand < 1 || maand > 12) return null;
    return { soort: "maand", maand: maandSleutel(Number(m[1]), maand) };
  }

  // juli 2026, en 2026 juli voor wie het andersom typt
  m = /^([a-zA-Z.]+) (\d{4})$/.exec(t) || /^(\d{4}) ([a-zA-Z.]+)$/.exec(t);
  if (m) {
    const isJaarEerst = /^\d{4}$/.test(m[1]);
    const maand = maandUitNaam(isJaarEerst ? m[2] : m[1]);
    const jaar = Number(isJaarEerst ? m[1] : m[2]);
    if (!maand) return null;
    return { soort: "maand", maand: maandSleutel(jaar, maand) };
  }

  return null;
}

/**
 * DE DAG UIT DE LIJST DIE HET DICHTST BIJ DE GEVRAAGDE LIGT.
 *
 * Franks eis van 24 augustus 2026: staat er op die datum niets, dan zegt het
 * scherm dat met zoveel woorden en springt het naar de dichtstbijzijnde dag die
 * er wel is. Deze functie beantwoordt het tweede deel; het eerste deel is het
 * scherm en die twee horen niet door elkaar te lopen.
 *
 * **BIJ EEN GELIJKE AFSTAND WINT DE NIEUWSTE.** Dat is een keuze en geen
 * toevalligheid van de sorteervolgorde: terugbladeren gaat over wat er sinds
 * toen is gebeurd, dus vooruit is de nuttigere kant. De toets legt dat vast.
 */
export function dichtstbijDag(dagen, gevraagd) {
  const doel = dagNummer(gevraagd);
  if (doel === null) return null;
  let beste = null;
  let besteAfstand = Infinity;
  for (const d of Array.isArray(dagen) ? dagen : []) {
    const datum = String((d && d.date) || d || "");
    const n = dagNummer(datum);
    if (n === null) continue;
    const afstand = Math.abs(n - doel);
    // `<` en niet `<=`: bij gelijke afstand houdt de eerste stand. Omdat de
    // lijst nieuwste-eerst binnenkomt, is dat de nieuwste. Dat is bedoeld.
    if (afstand < besteAfstand) {
      besteAfstand = afstand;
      beste = datum;
    }
  }
  return beste === null ? null : { datum: beste, afstand: besteAfstand };
}

/**
 * WAT MOET HET SCHERM DOEN MET WAT ER IS GETYPT?
 *
 * Alles bij elkaar, als een pure functie, zodat het scherm alleen nog hoeft te
 * TONEN wat hier is besloten. De vier uitkomsten:
 *
 *     {soort: "onleesbaar"}                    zeg welke vormen wel werken
 *     {soort: "maand", maand}                  klap open en spring erheen
 *     {soort: "dag", datum}                    open die dag
 *     {soort: "dag", datum, gevraagd, ...}     die dag was leeg; dit is de
 *                                              dichtstbijzijnde, en `gevraagd`
 *                                              is wat er is getypt
 *
 * `leeg` betekent: de lijst is leeg, er is nergens heen te springen.
 */
export function zoekSprong(dagen, tekst) {
  const gelezen = leesDatum(tekst);
  if (!gelezen) return { soort: "onleesbaar" };

  const lijst = Array.isArray(dagen) ? dagen : [];
  const datums = lijst.map((d) => String((d && d.date) || d || ""));

  if (gelezen.soort === "maand") {
    if (datums.some((d) => d.startsWith(`${gelezen.maand}-`))) {
      return { soort: "maand", maand: gelezen.maand };
    }
    // Een maand zonder enige dag: zoek vanaf de EERSTE van die maand.
    const dichtbij = dichtstbijDag(lijst, `${gelezen.maand}-01`);
    if (!dichtbij) return { soort: "leeg", gevraagd: gelezen.maand, gevraagdSoort: "maand" };
    return {
      soort: "dag",
      datum: dichtbij.datum,
      gevraagd: gelezen.maand,
      gevraagdSoort: "maand",
      afstand: dichtbij.afstand,
    };
  }

  if (datums.includes(gelezen.datum)) return { soort: "dag", datum: gelezen.datum };

  const dichtbij = dichtstbijDag(lijst, gelezen.datum);
  if (!dichtbij) return { soort: "leeg", gevraagd: gelezen.datum, gevraagdSoort: "dag" };
  return {
    soort: "dag",
    datum: dichtbij.datum,
    gevraagd: gelezen.datum,
    gevraagdSoort: "dag",
    afstand: dichtbij.afstand,
  };
}

/**
 * `1 bericht` tegen `4 berichten`, zonder een `1 berichten` ertussen.
 *
 * Klein, en het staat hier omdat een telling in een kop anders op drie plekken
 * met de hand wordt verbogen en er dan ergens een fout in sluipt.
 */
export function telWoord(aantal, enkelvoud, meervoud) {
  const n = Number(aantal) || 0;
  return `${n} ${n === 1 ? enkelvoud : meervoud}`;
}
